import axios, { AxiosError, type AxiosRequestConfig } from 'axios';
import { jwtDecode } from 'jwt-decode';

import i18n from '@/app/config/i18n';
import { queryClient } from '@/app/config/react-query';
import { getJWTQuery } from '@/queries/get-jwt';
import { useUserStore } from '@/store/userStore';
import type { RetryQueueItem } from '@/types';

const retryQueue: RetryQueueItem[] = [];

let isRefreshing = false;

const api = axios.create({
  baseURL: `${import.meta.env.VITE_API_URL}/api/v1`,
});

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest: AxiosRequestConfig | undefined = error.config;

    if (error.response?.status === 401 && originalRequest) {
      // If we're already refreshing, queue the request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          retryQueue.push({ config: originalRequest, resolve, reject });
        });
      }

      isRefreshing = true;
      try {
        const {
          data: { jwtToken },
        } = await queryClient.fetchQuery(getJWTQuery);

        if (!jwtToken) {
          throw new Error('No JWT available');
        }

        const {
          currentContext: { companyId, roles },
        } = jwtDecode<{ currentContext: { companyId: string; roles: string[] } }>(jwtToken) || {
          currentContext: { companyId: '', roles: [] },
        };

        // Have to use setState to persist the state
        useUserStore.setState(() => ({
          jwtToken,
          isAuthenticated: true,
          supplierId: companyId,
          isAdmin: roles.includes('ROLE_ADMIN'),
        }));

        originalRequest.headers = {
          ...originalRequest.headers,
          Authorization: `Bearer ${jwtToken}`,
        };

        // Request all the queued requests with the new token
        retryQueue.forEach(({ config, resolve, reject }) => {
          config.headers = {
            ...config.headers,
            Authorization: `Bearer ${jwtToken}`,
          };
          api(config).then(resolve).catch(reject);
        });

        // Clear the queue
        retryQueue.length = 0;

        return api(originalRequest);
      } catch (err) {
        retryQueue.forEach(({ reject }) => reject(err));
        retryQueue.length = 0;
        useUserStore.getState().logout();
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

api.interceptors.request.use((config) => {
  const { jwtToken } = useUserStore.getState();

  if (jwtToken) {
    config.headers['Authorization'] = `Bearer ${jwtToken}`;
  }

  config.headers['Accept-Language'] = i18n.language || 'en';

  return config;
});

export default api;
